import { Stack, Text } from "@chakra-ui/react";
import { format } from "date-fns";
import * as React from "react";

import {
  OptimizationRuleSyncedEvent,
  getOptimizationRuleSyncedEvents,
} from "../api/optimization-rule-synced-event";
import { useLoading, useToaster } from "../hooks";
import { OptimizationRule } from "../types/optimization-rule";
import DataTable from "./data-table";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";

interface OptimizationRuleSyncedEventsDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  rule: OptimizationRule;
  workspace: string;
}

export function OptimizationRuleSyncedEventsDialog({
  isOpen,
  onOpenChange,
  rule,
  workspace,
}: OptimizationRuleSyncedEventsDialogProps) {
  const wrapToast = useToaster();
  const [events, setEvents] = React.useState<OptimizationRuleSyncedEvent[]>([]);
  const [isLoading, wrapLoading] = useLoading();

  React.useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    wrapToast(async () => {
      await wrapLoading(async () => {
        const data = await getOptimizationRuleSyncedEvents(workspace, rule.id);
        if (isMounted) {
          setEvents(data);
        }
      });
    });

    return () => {
      isMounted = false;
    };
  }, [isOpen, rule.id, wrapLoading, wrapToast, workspace]);

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="xl">
      <DialogContent>
        <DialogCloseTrigger colorPalette="gray" />
        <DialogHeader>
          <DialogTitle>{rule.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap="4" height="md">
            {isLoading ? (
              <Text>Loading events...</Text>
            ) : events.length === 0 ? (
              <Text>No synced events found.</Text>
            ) : (
              <Stack overflowY="auto" flex="1">
                <DataTable
                  data={events}
                  columns={[
                    {
                      name: "Timestamp",
                      width: 180,
                      cell: ({ value: event }) => {
                        return (
                          <Text>
                            {format(event.timestamp, "MMM d, yyyy HH:mm:ss")}
                          </Text>
                        );
                      },
                    },
                    {
                      name: "Value",
                      width: 100,
                      cell: ({ value: event }) => {
                        return (
                          <Text>
                            {event["custom-data"]?.value?.toFixed(2) || "-"}
                          </Text>
                        );
                      },
                    },
                    {
                      name: "FBP",
                      width: 120,
                      cell: ({ value: event }) => {
                        return (
                          <Text truncate>{event["user-data"]?.fbp || "-"}</Text>
                        );
                      },
                    },
                    {
                      name: "FBC",
                      width: 120,
                      cell: ({ value: event }) => {
                        return (
                          <Text truncate>{event["user-data"]?.fbc || "-"}</Text>
                        );
                      },
                    },
                    {
                      name: "HEM",
                      width: 150,
                      cell: ({ value: event }) => {
                        return (
                          <Text truncate>{event["user-data"]?.em || "-"}</Text>
                        );
                      },
                    },
                  ]}
                />
              </Stack>
            )}
          </Stack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
}
